import type { Problem } from '@/types';

/* Practice Gym ladder: Anomaly Detection (module m14). Problem-forward, progressive.
   Auto-included via src/data/gym/index.ts; referenced by the `anom` ladder. */
export const anomalyProblems: Problem[] = [
  {
    "id": "an1",
    "ladder": "anom",
    "pos": 1,
    "stage": "Recognition",
    "lvl": 0,
    "difficulty": "recognition",
    "priority": "required",
    "source": "interview-style",
    "module": "m14",
    "title": "What makes a spike anomalous?",
    "concept": ["anomaly"],
    "obj": ["Charge"],
    "metric": "n/a",
    "edge": ["baseline", "volume floor"],
    "timed": false,
    "est": "3 min",
    "business": "An on-call engineer asks: today's failures look high — is it actually abnormal, or just noise?",
    "schema": ["charges"],
    "prompt": "No SQL yet. In plain English: define a baseline that excludes today, define a z-score, name a threshold, and say why a volume floor is needed.",
    "deliverable": "A short explanation: trailing baseline (excludes the current period), z-score = (today − baseline_mean)/baseline_std, a |z| threshold, and a minimum-volume floor.",
    "why": "'High' is meaningless without a baseline and a scale; recognise the four pieces before writing SQL.",
    "harder": "First rung — name the baseline, the z-score, the threshold and the floor.",
    "before": [
      "If today is inside the baseline, it hides its own spike — so the baseline must exclude today.",
      "On tiny volumes, a one-event change looks huge; a floor suppresses that."
    ],
    "hints": [
      "Baseline = trailing mean and std over prior days, excluding the current day.",
      "z = (today − baseline_mean) / baseline_std; flag when |z| exceeds, say, 3."
    ],
    "model": "An anomaly is a value far from what recent history predicts. Build a trailing baseline over prior periods that EXCLUDES the current period (otherwise the spike contaminates its own baseline), summarise it with a mean and standard deviation, and score the current period as z = (current − baseline_mean) / baseline_std. Flag when |z| exceeds a threshold (3 is common). Add a volume floor — ignore periods with too few events — because on small counts a single event produces a huge z that is noise, not signal.",
    "grain": "Conceptual — no output rows.",
    "confusion": "Including the current period in its own baseline is the classic mistake; it suppresses real spikes.",
    "explain": "Say the four pieces aloud: trailing baseline excluding today, mean+std, z-score, threshold + floor.",
    "teaches": "Recognising baseline exclusion, the z-score, a threshold and a volume floor.",
    "mode": "SQL"
  },
  {
    "id": "an2",
    "ladder": "anom",
    "pos": 2,
    "stage": "Mechanical",
    "lvl": 1,
    "difficulty": "easy",
    "priority": "required",
    "source": "pgexercises-style",
    "module": "m14",
    "title": "Daily failed-charge counts",
    "concept": ["anomaly"],
    "obj": ["Charge"],
    "metric": "count",
    "edge": ["bucketing"],
    "timed": false,
    "est": "4 min",
    "business": "Before detecting spikes you need the time series: failed charges per day for the last 30 days.",
    "schema": ["charges"],
    "prompt": "Return one row per day with the count of failed charges over the last 30 days, ordered by day.",
    "deliverable": "One row per day: day, failures.",
    "why": "The daily series is the input every anomaly score is computed over.",
    "harder": "Introduces the per-day bucket the baseline window will run over.",
    "before": ["Bucket with DATE_TRUNC('day', created_at) and filter status='failed'."],
    "hints": [
      "WHERE status='failed' AND created_at >= NOW() - INTERVAL '30 days'.",
      "GROUP BY DATE_TRUNC('day', created_at)."
    ],
    "solution": "SELECT DATE_TRUNC('day', created_at) AS day,\n       COUNT(*) AS failures\nFROM charges\nWHERE status='failed'\n  AND created_at >= NOW() - INTERVAL '30 days'\nGROUP BY DATE_TRUNC('day', created_at)\nORDER BY day;",
    "grain": "One row per day.",
    "confusion": "This is the raw series; no baseline yet.",
    "explain": "State that this daily series feeds the trailing baseline next.",
    "teaches": "Building the daily count series for anomaly scoring.",
    "mode": "SQL"
  },
  {
    "id": "an3",
    "ladder": "anom",
    "pos": 3,
    "stage": "Simple application",
    "lvl": 2,
    "difficulty": "medium",
    "priority": "required",
    "source": "Mode-style",
    "module": "m14",
    "title": "Trailing baseline that excludes today",
    "concept": ["anomaly"],
    "obj": ["Charge"],
    "metric": "trailing mean",
    "edge": ["frame exclusion", "ordering"],
    "timed": false,
    "est": "8 min",
    "business": "Compute each day's expected failures from the 7 days before it — not including the day itself.",
    "schema": ["charges"],
    "prompt": "From the daily failure series, add a baseline_mean = average of the 7 days strictly before each day, using a window frame that excludes the current row.",
    "deliverable": "One row per day: day, failures, baseline_mean.",
    "why": "Excluding the current row from its own baseline is the load-bearing detail; the window frame does it.",
    "harder": "Introduces ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING to exclude the current period.",
    "before": [
      "Aggregate to a daily series first (CTE), then run a window over it.",
      "ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING stops at the row before the current one."
    ],
    "howto": [
      "daily CTE: day, failures.",
      "AVG(failures) OVER (ORDER BY day ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING)."
    ],
    "hints": [
      "The frame 1 PRECEDING (not CURRENT ROW) is what excludes today.",
      "Order the window by day."
    ],
    "solution": "WITH daily AS (\n  SELECT DATE_TRUNC('day', created_at) AS day, COUNT(*) AS failures\n  FROM charges WHERE status='failed' AND created_at >= NOW() - INTERVAL '60 days'\n  GROUP BY DATE_TRUNC('day', created_at)\n)\nSELECT day, failures,\n       AVG(failures) OVER (\n         ORDER BY day\n         ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING\n       ) AS baseline_mean\nFROM daily\nORDER BY day;",
    "verify": {
      "grain": "One row per day.",
      "columns": ["day", "failures", "baseline_mean"],
      "sample": { "cols": ["day", "failures", "baseline_mean"], "rows": [["2024-05-20", "42", "40.1429"], ["2024-05-21", "120", "41.0000"]] },
      "commonWrong": ["Using CURRENT ROW (includes today in its own baseline).", "Ordering the frame by something other than day."],
      "validation": ["baseline_mean excludes the current day", "earliest rows have a NULL/partial baseline"],
      "edgeCases": ["The first 7 days have fewer than 7 prior rows — the baseline is partial.", "A gap-day with zero failures is missing unless the series is dense."],
      "checklist": ["daily series", "frame 7 PRECEDING AND 1 PRECEDING", "ordered by day"]
    },
    "confusion": "1 PRECEDING (not CURRENT ROW) is what keeps today out of its own baseline.",
    "explain": "Read the frame bounds aloud and say why 1 PRECEDING matters.",
    "teaches": "A trailing baseline mean that excludes the current period.",
    "mode": "SQL"
  },
  {
    "id": "an4",
    "ladder": "anom",
    "pos": 4,
    "stage": "Applied metric",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "required",
    "source": "DataLemur-style",
    "module": "m14",
    "title": "Z-score and flag anomalous days",
    "concept": ["anomaly"],
    "obj": ["Charge"],
    "metric": "z-score",
    "edge": ["zero std", "threshold"],
    "timed": false,
    "est": "9 min",
    "business": "Score each day against its trailing baseline and flag days more than 3 standard deviations above it.",
    "schema": ["charges"],
    "prompt": "Add baseline_std (same trailing frame) and z = (failures − baseline_mean)/baseline_std, then flag is_anomaly = z > 3. Guard a zero/NULL std.",
    "deliverable": "One row per day: day, failures, z, is_anomaly.",
    "why": "The z-score on a current-excluded baseline is the core anomaly signal; guarding zero std is the trap.",
    "harder": "Adds STDDEV over the same frame and a guarded division.",
    "before": [
      "baseline_std uses the same ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING frame.",
      "Divide by NULLIF(baseline_std,0) so a flat baseline doesn't error."
    ],
    "howto": [
      "daily CTE, then baseline mean and std in a scored CTE.",
      "z = (failures − baseline_mean)/NULLIF(baseline_std,0); flag z > 3."
    ],
    "hints": [
      "STDDEV(failures) OVER (... ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING).",
      "is_anomaly = z > 3; NULL z (no baseline yet) is not an anomaly."
    ],
    "solution": "WITH daily AS (\n  SELECT DATE_TRUNC('day', created_at) AS day, COUNT(*) AS failures\n  FROM charges WHERE status='failed' AND created_at >= NOW() - INTERVAL '60 days'\n  GROUP BY DATE_TRUNC('day', created_at)\n),\nscored AS (\n  SELECT day, failures,\n         AVG(failures)    OVER w AS base_mean,\n         STDDEV(failures) OVER w AS base_std\n  FROM daily\n  WINDOW w AS (ORDER BY day ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING)\n)\nSELECT day, failures,\n       ROUND((failures - base_mean) / NULLIF(base_std,0), 2) AS z,\n       ((failures - base_mean) / NULLIF(base_std,0)) > 3 AS is_anomaly\nFROM scored\nORDER BY day;",
    "verify": {
      "grain": "One row per day.",
      "columns": ["day", "failures", "z", "is_anomaly"],
      "sample": { "cols": ["day", "failures", "z", "is_anomaly"], "rows": [["2024-05-20", "42", "0.12", "false"], ["2024-05-21", "120", "6.80", "true"]] },
      "commonWrong": ["Dividing by a zero std (errors or +/-inf).", "Including the current day in mean/std."],
      "validation": ["z null where baseline is absent", "is_anomaly only where z > 3"],
      "edgeCases": ["A flat baseline (std 0) → NULLIF guards it, z is NULL.", "First days have no baseline → not flagged."],
      "checklist": ["std over the excluding frame", "guarded divide", "threshold z > 3"]
    },
    "confusion": "A zero std means no variation to measure against — guard it, don't divide by it.",
    "explain": "Name the numerator, denominator and threshold of the z-score aloud.",
    "teaches": "Turning a trailing baseline into a guarded z-score and a flag.",
    "mode": "SQL"
  },
  {
    "id": "an5",
    "ladder": "anom",
    "pos": 5,
    "stage": "Multi-step",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "should",
    "source": "Mode-style",
    "module": "m14",
    "title": "Dispute-rate spike with a volume floor",
    "concept": ["anomaly"],
    "obj": ["Charge", "Dispute"],
    "metric": "rate z-score",
    "edge": ["volume floor", "rate vs count"],
    "timed": false,
    "est": "11 min",
    "business": "Detect days where the dispute RATE (disputes ÷ succeeded charges) spikes, ignoring low-volume days that are just noise.",
    "schema": ["charges", "disputes"],
    "prompt": "Build a daily dispute_rate = distinct disputed charges ÷ succeeded charges, score it against its trailing baseline, and flag is_anomaly only when z > 3 AND succeeded charges that day ≥ 200.",
    "deliverable": "One row per day: day, succeeded, dispute_rate, z, is_anomaly.",
    "why": "Rate-based detection plus a volume floor is how real spike alerts avoid firing on thin days.",
    "harder": "Combines a daily rate, a trailing baseline on the rate, and a volume-floor gate on the flag.",
    "before": [
      "Compute the daily rate first, then run the trailing baseline over the rate.",
      "The floor gates the FLAG, not the baseline — keep low-volume days out of the alert."
    ],
    "howto": [
      "daily CTE: succeeded charges and distinct disputed charges → dispute_rate.",
      "Trailing mean/std over dispute_rate; z as before.",
      "is_anomaly = z > 3 AND succeeded >= 200."
    ],
    "hints": [
      "Count distinct disputed charge_ids to avoid fan-out from multiple disputes.",
      "Gate the flag with the volume floor: AND succeeded >= 200."
    ],
    "solution": "WITH daily AS (\n  SELECT DATE_TRUNC('day', c.created_at) AS day,\n         COUNT(*) FILTER (WHERE c.status='succeeded') AS succeeded,\n         COUNT(DISTINCT d.charge_id) AS disputed\n  FROM charges c\n  LEFT JOIN disputes d ON d.charge_id = c.charge_id\n  WHERE c.created_at >= NOW() - INTERVAL '60 days'\n  GROUP BY DATE_TRUNC('day', c.created_at)\n),\nrate AS (\n  SELECT day, succeeded,\n         disputed::numeric / NULLIF(succeeded,0) AS dispute_rate\n  FROM daily\n),\nscored AS (\n  SELECT day, succeeded, dispute_rate,\n         AVG(dispute_rate)    OVER w AS base_mean,\n         STDDEV(dispute_rate) OVER w AS base_std\n  FROM rate\n  WINDOW w AS (ORDER BY day ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING)\n)\nSELECT day, succeeded,\n       ROUND(dispute_rate, 4) AS dispute_rate,\n       ROUND((dispute_rate - base_mean)/NULLIF(base_std,0), 2) AS z,\n       ((dispute_rate - base_mean)/NULLIF(base_std,0) > 3 AND succeeded >= 200) AS is_anomaly\nFROM scored\nORDER BY day;",
    "verify": {
      "grain": "One row per day.",
      "columns": ["day", "succeeded", "dispute_rate", "z", "is_anomaly"],
      "sample": { "cols": ["day", "succeeded", "dispute_rate", "z", "is_anomaly"], "rows": [["2024-05-20", "1240", "0.0081", "0.30", "false"], ["2024-05-21", "1310", "0.0420", "5.10", "true"]] },
      "commonWrong": ["Applying the floor to the baseline instead of the flag.", "Counting disputes (not distinct disputed charges), inflating the rate."],
      "validation": ["dispute_rate ∈ [0,1]", "low-volume days never flagged"],
      "edgeCases": ["A 1-dispute day on 12 charges has a huge z but is gated out by the floor.", "A flat baseline std → NULLIF guards z."],
      "checklist": ["daily rate", "trailing baseline on the rate", "volume-floor gate on the flag"]
    },
    "confusion": "The floor belongs on the flag, not the baseline — you still learn from quiet days, you just don't alert on them.",
    "explain": "Explain why a one-dispute day on tiny volume must not fire an alert.",
    "teaches": "Rate-based anomaly detection with a volume floor.",
    "mode": "SQL"
  },
  {
    "id": "an6",
    "ladder": "anom",
    "pos": 6,
    "stage": "Debug",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "should",
    "source": "interview-style",
    "module": "m14",
    "title": "Debug: the spike hides in its own baseline",
    "concept": ["anomaly"],
    "obj": ["Charge"],
    "metric": "z-score",
    "edge": ["frame exclusion"],
    "timed": false,
    "est": "7 min",
    "business": "A z-score monitor never fires on the day of a real outage — the spike is suppressing its own score.",
    "schema": ["charges"],
    "prompt": "Diagnose the bug in one sentence, then fix the window frame so the baseline excludes the current day.",
    "broken": "WITH daily AS (\n  SELECT DATE_TRUNC('day', created_at) AS day, COUNT(*) AS failures\n  FROM charges WHERE status='failed' AND created_at >= NOW() - INTERVAL '60 days'\n  GROUP BY DATE_TRUNC('day', created_at)\n)\nSELECT day, failures,\n       ROUND((failures - AVG(failures) OVER w) / NULLIF(STDDEV(failures) OVER w,0), 2) AS z\nFROM daily\nWINDOW w AS (ORDER BY day ROWS BETWEEN 7 PRECEDING AND CURRENT ROW);",
    "deliverable": "A one-sentence diagnosis + a corrected frame ending at 1 PRECEDING.",
    "why": "CURRENT ROW pulls the spike into its own mean and std, deflating z exactly when you need it.",
    "harder": "You must see that the frame boundary, not the formula, is the bug.",
    "before": ["A big spike inside the baseline raises the mean and the std, shrinking its own z."],
    "hints": [
      "The frame ends at CURRENT ROW, so today is in its own baseline.",
      "Change the end bound to 1 PRECEDING."
    ],
    "solution": "WITH daily AS (\n  SELECT DATE_TRUNC('day', created_at) AS day, COUNT(*) AS failures\n  FROM charges WHERE status='failed' AND created_at >= NOW() - INTERVAL '60 days'\n  GROUP BY DATE_TRUNC('day', created_at)\n)\nSELECT day, failures,\n       ROUND((failures - AVG(failures) OVER w) / NULLIF(STDDEV(failures) OVER w,0), 2) AS z\nFROM daily\nWINDOW w AS (ORDER BY day ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING);",
    "verify": {
      "grain": "One row per day.",
      "columns": ["day", "failures", "z"],
      "commonWrong": ["Leaving the end bound at CURRENT ROW.", "Blaming the threshold instead of the frame."],
      "validation": ["baseline excludes the current day", "a real spike now produces a large z"],
      "edgeCases": ["The bigger the spike, the more CURRENT ROW deflates its own z — the worst case for the bug."],
      "checklist": ["named the self-contamination", "end bound 1 PRECEDING", "spike now flags"]
    },
    "confusion": "The formula was right; the frame boundary (CURRENT ROW vs 1 PRECEDING) was the bug.",
    "explain": "State the self-contamination before changing the bound.",
    "teaches": "Why the baseline frame must end at 1 PRECEDING.",
    "mode": "SQL"
  },
  {
    "id": "an7",
    "ladder": "anom",
    "pos": 7,
    "stage": "Edge-case hard",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "stretch",
    "source": "StrataScratch-style",
    "module": "m14",
    "title": "Failure-code spike, per code, with a floor",
    "concept": ["anomaly"],
    "obj": ["Charge"],
    "metric": "per-code z-score",
    "edge": ["partition", "rare codes"],
    "timed": false,
    "est": "10 min",
    "business": "Detect when a specific failure_code (e.g. 'card_declined') spikes, scored within its own history, ignoring rare codes.",
    "schema": ["charges"],
    "prompt": "Per failure_code per day, count failures, build a trailing baseline PARTITIONED BY failure_code, score z, and flag only when z > 3 AND that day's count for the code ≥ 30.",
    "deliverable": "One row per (failure_code, day): failure_code, day, failures, z, is_anomaly.",
    "why": "Per-entity baselines (partitioning) plus a floor on rare codes is the realistic multi-series detector.",
    "harder": "Adds PARTITION BY so each code is scored against its own history, not the global one.",
    "before": [
      "Each code has its own baseline — PARTITION BY failure_code inside the window.",
      "Rare codes spike easily; the floor on the code's daily count gates the flag."
    ],
    "howto": [
      "daily CTE grouped by failure_code and day (exclude NULL codes).",
      "Window PARTITION BY failure_code ORDER BY day, frame 7 PRECEDING AND 1 PRECEDING.",
      "Flag z > 3 AND failures >= 30."
    ],
    "hints": [
      "PARTITION BY failure_code keeps each code's baseline separate.",
      "Filter out NULL failure_code (succeeded charges have none)."
    ],
    "solution": "WITH daily AS (\n  SELECT failure_code, DATE_TRUNC('day', created_at) AS day, COUNT(*) AS failures\n  FROM charges\n  WHERE status='failed' AND failure_code IS NOT NULL\n    AND created_at >= NOW() - INTERVAL '60 days'\n  GROUP BY failure_code, DATE_TRUNC('day', created_at)\n),\nscored AS (\n  SELECT failure_code, day, failures,\n         AVG(failures)    OVER w AS base_mean,\n         STDDEV(failures) OVER w AS base_std\n  FROM daily\n  WINDOW w AS (PARTITION BY failure_code ORDER BY day ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING)\n)\nSELECT failure_code, day, failures,\n       ROUND((failures - base_mean)/NULLIF(base_std,0), 2) AS z,\n       ((failures - base_mean)/NULLIF(base_std,0) > 3 AND failures >= 30) AS is_anomaly\nFROM scored\nORDER BY failure_code, day;",
    "verify": {
      "grain": "One row per (failure_code, day).",
      "columns": ["failure_code", "day", "failures", "z", "is_anomaly"],
      "sample": { "cols": ["failure_code", "day", "failures", "z", "is_anomaly"], "rows": [["card_declined", "2024-05-21", "410", "5.90", "true"], ["lost_card", "2024-05-21", "7", "4.20", "false"]] },
      "commonWrong": ["Scoring against the global baseline instead of per code.", "Flagging rare codes that clear z but not the floor."],
      "validation": ["each code scored within its own partition", "rare-code spikes gated by the floor"],
      "edgeCases": ["A brand-new code has no baseline → z NULL, not flagged.", "A code with 7 fraud events spiking is suppressed by the count floor."],
      "checklist": ["partition by code", "exclude NULL codes", "floor on the code's daily count"]
    },
    "confusion": "Each code must be scored against its own history — a global baseline drowns small codes.",
    "explain": "Explain why partitioning by code changes which days flag.",
    "teaches": "Per-entity (partitioned) anomaly detection with a count floor.",
    "mode": "SQL"
  },
  {
    "id": "an8",
    "ladder": "anom",
    "pos": 8,
    "stage": "Final boss",
    "lvl": 5,
    "difficulty": "final-boss",
    "priority": "boss",
    "source": "Stripe-interview-style",
    "module": "m14",
    "title": "Final boss (timed 12 min): daily failure-spike monitor",
    "concept": ["anomaly"],
    "obj": ["Charge"],
    "metric": "z-score",
    "edge": ["baseline exclusion", "volume floor", "immature window"],
    "timed": true,
    "est": "12 min",
    "business": "\"Build the alert that pages us on a failure spike.\" Return only the flagged days and defend the baseline, the floor and the false-positive trade-off.",
    "schema": ["charges"],
    "prompt": "Over the last 30 days, return only days where failed charges are anomalous: z = (failures − trailing_mean)/trailing_std with a 7-day baseline EXCLUDING the day, flagged when z > 3 AND failures ≥ 50. Output the flagged days with their z, and be ready to defend the threshold and floor.",
    "deliverable": "One row per flagged day: day, failures, baseline_mean, z.",
    "why": "This fuses the trailing baseline, the guarded z-score, the volume floor and the precision/recall trade-off — a complete anomaly answer.",
    "harder": "Combines the excluding frame, the guarded z, the floor and a filter to only anomalies, under time pressure.",
    "before": [
      "Baseline mean and std over ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING.",
      "Flag z > 3 AND failures >= 50, then keep only flagged rows.",
      "A higher threshold trades recall for fewer false pages — say where you'd set it."
    ],
    "howto": [
      "daily CTE over a 60-day lookback (so the newest days have a baseline).",
      "scored CTE with mean/std on the excluding frame and a guarded z.",
      "Filter to z > 3 AND failures >= 50."
    ],
    "hints": [
      "Look back further than 30 days so the reported window has full baselines.",
      "WHERE on the outer query keeps only the anomalies.",
      "Be ready to justify threshold 3 and floor 50 as a false-positive trade-off."
    ],
    "solution": "WITH daily AS (\n  SELECT DATE_TRUNC('day', created_at) AS day, COUNT(*) AS failures\n  FROM charges WHERE status='failed' AND created_at >= NOW() - INTERVAL '60 days'\n  GROUP BY DATE_TRUNC('day', created_at)\n),\nscored AS (\n  SELECT day, failures,\n         AVG(failures)    OVER w AS baseline_mean,\n         STDDEV(failures) OVER w AS baseline_std\n  FROM daily\n  WINDOW w AS (ORDER BY day ROWS BETWEEN 7 PRECEDING AND 1 PRECEDING)\n)\nSELECT day, failures,\n       ROUND(baseline_mean, 2) AS baseline_mean,\n       ROUND((failures - baseline_mean)/NULLIF(baseline_std,0), 2) AS z\nFROM scored\nWHERE day >= NOW() - INTERVAL '30 days'\n  AND (failures - baseline_mean)/NULLIF(baseline_std,0) > 3\n  AND failures >= 50\nORDER BY day;",
    "rubric": [
      "Daily failed-charge series",
      "Baseline mean and std over a trailing frame EXCLUDING the current day (1 PRECEDING)",
      "z = (failures − mean)/std with NULLIF guard on std",
      "Volume floor (failures >= 50) so thin days don't page",
      "Only anomalous days returned",
      "Threshold/floor defended as a false-positive vs missed-spike trade-off"
    ],
    "verify": {
      "grain": "One row per flagged (anomalous) day.",
      "columns": ["day", "failures", "baseline_mean", "z"],
      "sample": { "cols": ["day", "failures", "baseline_mean", "z"], "rows": [["2024-05-21", "120", "41.00", "6.80"], ["2024-06-02", "300", "55.00", "9.10"]] },
      "commonWrong": ["Baseline including the current day.", "No volume floor, so quiet days page.", "Returning all days instead of only anomalies."],
      "validation": ["every returned row has z > 3 and failures >= 50", "baseline excludes the day"],
      "edgeCases": ["A 60-day lookback gives the reported 30-day window full baselines.", "The newest day is still scorable because it has 7 prior days."],
      "checklist": ["excluding frame", "guarded z", "volume floor", "anomalies-only output", "defends the threshold"]
    },
    "confusion": "Lookback (60d) and reporting window (30d) differ so the reported days all have complete baselines.",
    "explain": "Defend the threshold and floor as a false-positive trade-off, as if to an interviewer.",
    "teaches": "A complete daily anomaly monitor with baseline, floor and threshold defence.",
    "mode": "SQL"
  }
];
